// src/components/home/Notifications.js
import React, { useState } from "react";
import { useAtom } from "jotai";
import moment from "moment";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
    appConstants,
    activeChildDetail,
    NotificationAtom
} from "../../atomstore/store";
import { fetchChildTimeline } from "../../queryfunctions/queryfunctions";
import { useRequestProcessor } from "../../requestProcessor";
import UpdateCard from "../home/UpdateCard";
import PushNotificationCard from "./PushNotificationCard";
import SpinnerLoading from "./SpinnerLoading";

const ITEM_PER_PAGE = 50;

const Notifications = ({ setSelectedPhoto }) => {
    const navigate = useNavigate();

    const [ChildTimeline, setTimeline] = useAtom(NotificationAtom);
    const [constants] = useAtom(appConstants);
    const [activeChild] = useAtom(activeChildDetail);

    const [childTimeline, setChildTimeline] = useState({ startIndex: 0 });
    const [dataAvailable, setDataAvailable] = useState(true);
    const [length, setLength] = useState(0);

    const { useQueryWrapper } = useRequestProcessor();

    const { isLoading } = useQueryWrapper(
        ["NotificationsTimeline", activeChild?.id],
        () =>
            fetchChildTimeline(
                childTimeline.startIndex,
                setDataAvailable,
                ChildTimeline,
                ITEM_PER_PAGE,
                constants?.INCLUDE_TYPES_NOTIFICATION,
                setLength,
                setTimeline,
                null,
                true
            ),
        { keepPreviousData: true },
        {
            onSuccess: (data) => {
                if (!Array.isArray(data)) return;
                setChildTimeline(data);
            }
        }
    );

    const groupedNotifications = React.useMemo(() => {
        if (!Array.isArray(ChildTimeline)) return [];

        return ChildTimeline.filter((group) => group?.data?.length > 0).sort(
            (a, b) => {
                const dateA = moment(a.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"], true);
                const dateB = moment(b.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"], true);
                if (!dateA.isValid() || !dateB.isValid()) return 0;
                return dateB.valueOf() - dateA.valueOf();
            }
        );
    }, [ChildTimeline]);

    const formatDateLabel = (dateGroup) => {
        const parsed = moment(dateGroup, ["DD-MM-YY", "DD-MM-YYYY"], true);
        if (!parsed.isValid()) return dateGroup;
        if (parsed.isSame(moment(), "day")) return "Today";
        if (parsed.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
        return parsed.format("MMMM DD, YYYY");
    };

    const loadMore = () => {
        setChildTimeline(prev => ({
            ...prev,
            startIndex: (prev.startIndex || 0) + ITEM_PER_PAGE
        }));
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 flex flex-col">
            {isLoading && <SpinnerLoading />}
            <header className="shrink-0 bg-white border-b px-4 py-4 flex items-center space-x-3 z-30">
                <button type="button" onClick={() => navigate(-1)} aria-label="Go back">
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2
                    className="text-lg font-bold text-gray-800 cursor-pointer"
                    onClick={() => navigate(-1)}
                >
                    Notifications
                </h2>
            </header>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {groupedNotifications.length === 0 ? (
                    <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-600">
                        No updates available
                    </div>
                ) : (
                    groupedNotifications.map((dayGroup, groupIdx) => (
                        <section
                            key={`${dayGroup.dateGroup}-${groupIdx}`}
                            className="space-y-3"
                        >
                            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide pt-1 pb-2 border-b border-gray-200">
                                {formatDateLabel(dayGroup.dateGroup)}
                            </h3>
                            <div className="space-y-4">
                                {dayGroup.data.map((update, idx) =>
                                    update.source === "push" ? (
                                        <PushNotificationCard
                                            key={`push-${dayGroup.dateGroup}-${update.notification_id ?? idx}`}
                                            update={update}
                                        />
                                    ) : (
                                        <UpdateCard
                                            key={`cu-${dayGroup.dateGroup}-${idx}`}
                                            update={update}
                                            setSelectedPhoto={setSelectedPhoto}
                                        />
                                    )
                                )}
                            </div>
                        </section>
                    ))
                )}
            </div>
        </div>
    );
};

export default Notifications;
